import React from "react";
import { ProductVersionMetadataModel } from "./specsModel/productMetadata/ProductVersionMetadataModel";

export function normalizeMetadataSnapshot(
  snapshot: Record<string, any> | null
) {
  return snapshot && Object.keys(snapshot).length > 0 ? snapshot : null;
}

export function useProductVersionMetadataModel({
  rawVersionMetadata,
  updateRawVersionMetadata,
}: {
  rawVersionMetadata: Record<string, any> | null;
  updateRawVersionMetadata: (metadata: Record<string, any> | null) => void;
}) {
  const metadataModel = React.useMemo(
    () =>
      new ProductVersionMetadataModel({
        debugPathRoot: "window.specs.metadataModel",
      }),
    []
  );
  const [, setMetadataRevision] = React.useState(0);
  const lastSyncedMetadataRef = React.useRef<string | null>(null);
  const normalizedMetadata = React.useMemo(
    () => normalizeMetadataSnapshot(rawVersionMetadata),
    [rawVersionMetadata]
  );
  const normalizedMetadataJson = React.useMemo(
    () => JSON.stringify(normalizedMetadata),
    [normalizedMetadata]
  );

  React.useEffect(() => {
    if (normalizedMetadataJson === lastSyncedMetadataRef.current) {
      return;
    }

    metadataModel.restore(normalizedMetadata);
    setMetadataRevision(metadataModel.getRevision());
    lastSyncedMetadataRef.current = normalizedMetadataJson;
  }, [metadataModel, normalizedMetadata, normalizedMetadataJson]);

  React.useEffect(() => {
    return metadataModel.subscribe(() => {
      setMetadataRevision(metadataModel.getRevision());
      const nextSnapshot = normalizeMetadataSnapshot(metadataModel.save());
      const nextSnapshotJson = JSON.stringify(nextSnapshot);

      if (nextSnapshotJson === lastSyncedMetadataRef.current) {
        return;
      }

      lastSyncedMetadataRef.current = nextSnapshotJson;
      updateRawVersionMetadata(nextSnapshot);
    });
  }, [metadataModel, updateRawVersionMetadata]);

  React.useEffect(() => {
    const debugState = (window.specs ??= {});

    Object.defineProperty(debugState, "metadataModel", {
      configurable: true,
      enumerable: true,
      value: metadataModel,
    });
    Object.defineProperty(debugState, "metadataSnapshot", {
      configurable: true,
      enumerable: true,
      get: () => metadataModel.save(),
    });

    return () => {
      if (!window.specs) {
        return;
      }

      delete (window.specs as Record<string, unknown>).metadataModel;
      delete (window.specs as Record<string, unknown>).metadataSnapshot;
    };
  }, [metadataModel]);

  return metadataModel;
}
